import { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import type { DeskMobileSpec } from "../scenario/types";
import { useSim } from "../state/store";

/**
 * A smartphone lying on a surface (env.deskMobile). Where it lies follows the
 * last beat with `mobileAt`; it vibrates and lights up while the current beat
 * has `mobileBuzz` or a device dispatches `cf-desk-mobile` {buzz:true}.
 */
export function DeskMobile({ spec }: { spec: DeskMobileSpec }) {
  const group = useRef<THREE.Group>(null);
  const screen = useRef<THREE.MeshBasicMaterial>(null);
  const [eventBuzz, setEventBuzz] = useState(false);
  const scenario = useSim((s) => s.scenario);
  const beatId = useSim((s) => s.currentBeatId);

  const { spotId, beatBuzz } = useMemo(() => {
    const beats = scenario?.beats ?? [];
    const idx = beats.findIndex((b) => b.id === beatId);
    let at = spec.initial ?? Object.keys(spec.spots)[0];
    for (let i = idx; i >= 0; i--) {
      const m = beats[i]?.mobileAt;
      if (m) {
        at = m;
        break;
      }
    }
    return { spotId: at, beatBuzz: !!(idx >= 0 && beats[idx]?.mobileBuzz) };
  }, [scenario, beatId, spec]);

  useEffect(() => {
    const on = (e: Event) => setEventBuzz(!!(e as CustomEvent<{ buzz?: boolean }>).detail?.buzz);
    window.addEventListener("cf-desk-mobile", on);
    return () => window.removeEventListener("cf-desk-mobile", on);
  }, []);
  useEffect(() => setEventBuzz(false), [beatId]);

  const spot = spec.spots[spotId];
  const buzz = beatBuzz || eventBuzz;
  const base = useMemo(() => new THREE.Vector3(...(spot?.position ?? [0, 0, 0])), [spot]);

  useFrame(({ clock }) => {
    const g = group.current;
    if (!g) return;
    const t = clock.elapsedTime;
    // Short bursts of a few millimetres, like a phone buzzing on a table.
    const burst = buzz && Math.sin(t * 5.2) > 0.1;
    const dx = burst ? Math.sin(t * 95) * 0.0026 : 0;
    const dz = burst ? Math.cos(t * 83) * 0.0022 : 0;
    g.position.set(base.x + dx, base.y, base.z + dz);
    if (screen.current) {
      const target = buzz ? 1 : 0.06;
      const c = screen.current.color;
      const cur = c.r;
      const next = cur + (target - cur) * 0.12;
      c.setRGB(next * 0.75, next * 0.85, next);
    }
  });

  if (!spot) return null;
  const yaw = THREE.MathUtils.degToRad(spot.rotationDeg ?? 0);
  return (
    <group ref={group} position={spot.position} rotation={[0, yaw, 0]}>
      <mesh position={[0, 0.0045, 0]} castShadow>
        <boxGeometry args={[0.072, 0.009, 0.148]} />
        <meshStandardMaterial color="#1c1f24" roughness={0.35} metalness={0.6} />
      </mesh>
      <mesh position={[0, 0.0092, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[0.066, 0.141]} />
        <meshBasicMaterial ref={screen} color="#0b0d10" toneMapped={false} />
      </mesh>
      <mesh position={[0.018, -0.0005, -0.05]}>
        <cylinderGeometry args={[0.009, 0.009, 0.003, 16]} />
        <meshStandardMaterial color="#2a2e35" roughness={0.2} metalness={0.8} />
      </mesh>
    </group>
  );
}
